// What a session's tokens are WORTH at OpenRouter list rates — and where that
// figure came from.
//
// THE PROBLEM THIS EXISTS FOR. `PriceBasis` (beside this file) made "what did
// this cost" honest: a flat-rate coding subscription books `$0` by contract, an
// unpriceable model books NULL, and neither can be mistaken for a measurement.
// Honest, and useless as a magnitude. Measured on prod 2026-08-27, 151M tokens
// across `kimi` + `zai` + `runner` display as $0.00 — a session that did nothing
// and a session that burned 60M tokens render identically, because their
// MARGINAL cost really is zero. A maintainer cannot rank, budget, or notice
// anything from that column.
//
// So the worth rides a SEPARATE column. `cost_usd` stays "what this actually
// cost, or NULL when we cannot say"; `reference_usd` is "what these tokens are
// worth at OpenRouter list rates", computed whenever the model has a listing and
// INDEPENDENT of how the run was paid for. A $200/mo-plan session then reads
// cost $0, worth $47.20 — both true, neither confusable. Folding the reference
// into `cost_usd` would undo #2485 in one commit: the number a customer reads as
// a bill would once again be a number nobody was billed.
//
// WHY A SEPARATE VOCABULARY RATHER THAN REUSING `PriceBasis`. Considered, and
// rejected on the rule that kept `provider_report` out of `PriceBasis` until a
// writer could reach it (#3937): a member no writer can reach is an unrun branch, and
// a basis enum is the worst place to keep one because a reader takes the label as
// proof. Of `PriceBasis`'s members, `flat_rate` is unreachable here (a
// reference is never $0-by-contract — the subscription is exactly the fact this
// column looks past), `provider_fallback` is unreachable by construction (the
// reference refuses ceilings outright; an invented number is the failure this arc
// exists to remove), and `registry` would be an outright lie (the figure is NOT
// from `MODEL_REGISTRY` — see `OPENROUTER_REFERENCE`, whose rates disagree with
// it by up to 3x on the very rows the registry claims are OpenRouter's), and so
// would `provider_report` (a reference is a list price times our token counts,
// never what a call cost). Of what stays reachable, most would mean something
// narrower than its name. Four members, all reachable, is the honest shape.
//
// WHY TWO COLUMNS AND NOT THREE. A third column naming the OpenRouter LISTING
// each figure was priced from (`reference_model`) was considered and cut. The
// row already carries `model`, and `OPENROUTER_REFERENCE` maps a model id to its
// listing in version control — the same recoverability argument
// `apps/worker/src/fleet/usage-ingest.ts` already makes for `cost_usd` ("its
// price basis stays recoverable because the model id is on the durable event row
// and `MODEL_REGISTRY` is in version control"). The KNOWN limit of that choice,
// stated rather than discovered: if a model id is ever re-pointed at a different
// listing, a historical row's stored number is frozen but the listing recovered
// for it is today's. That is the same limit every price this platform stores
// already has, and it buys a column shape one field smaller in a table that is
// expensive to migrate.

import { z } from 'zod';

/**
 * How a session row's `reference_usd` was derived.
 *
 * Every member is reachable by a writer. `referenceQuote`
 * (`@developerz/config`) produces the first three; the migration that added the
 * column produces the fourth and nothing else ever writes it.
 */
export const ReferenceBasis = {
  /**
   * Valued from an `OPENROUTER_REFERENCE` row (`@developerz/config`): OpenRouter's
   * published list price x reported tokens. `reference_usd IS NOT NULL`.
   *
   * An ESTIMATE, and deliberately an estimate of something nobody was billed —
   * it answers "what would these tokens have cost on the open market", which is
   * the only question that has the same answer whether the run was metered, on a
   * flat-rate plan, or on the box's own subscription. Every figure in it is
   * sourced to a named listing and version-controlled.
   */
  Listed: 'listed',
  /**
   * Tokens were spent on a model OpenRouter does not list, so there is no rate to
   * value them at. `reference_usd IS NULL`.
   *
   * The honest answer, and the one that must never be approximated: the whole
   * point of this arc is that an invented number is worse than an absent one.
   * The live example on 2026-08-27 was an anonymous cloaked-alpha listing
   * OpenRouter publishes no price for: 907 prod sessions, 126M tokens, no rate.
   * That id has since been WITHDRAWN and is not named here — it no longer
   * resolves, so quoting it would hand a reader a model they cannot run. The
   * class it stands for is permanent; any unpriced listing lands on this member.
   */
  Unlisted: 'unlisted',
  /**
   * No tokens are recorded on this row: a session born but not yet settled, or a
   * synthetic audit-chain parent that runs no model (`gateway` / `human` /
   * `platform` / `rollout`). `reference_usd IS NULL`.
   *
   * NULL rather than `0`, unlike `cost_usd`'s `not_recorded`. Zero tokens really
   * are worth zero dollars, so `0` would be defensible — and it would put a
   * confident `$0.00` on exactly the rows this arc exists to stop putting one on.
   * One meaning per state: a number here means a number was computed.
   */
  NotRecorded: 'not_recorded',
  /**
   * Written before this column existed. `reference_usd IS NULL`.
   *
   * The reference is a pure function of `(model, token_in, token_out)`, so unlike
   * `PriceBasis.LegacyUnattributed` these rows are RECOVERABLE — a backfill could
   * value them. It is deliberately not done in the migration that adds the
   * column: an `UPDATE` over the table would rewrite every row under an ACCESS
   * EXCLUSIVE lock to restate something a reader can derive, and the label says
   * exactly which rows those are (`count(*) WHERE reference_basis =
   * 'legacy_unattributed'`) if that ever becomes worth a backfill lane.
   *
   * Nothing writes this value. It only ever shrinks.
   */
  LegacyUnattributed: 'legacy_unattributed',
} as const;

export const ReferenceBasisSchema = z.enum(ReferenceBasis);
export type ReferenceBasis = z.infer<typeof ReferenceBasisSchema>;

/**
 * May a `reference_usd` on this basis be rendered as a figure?
 *
 * Exactly one basis carries a number, and the DB CHECK constraint
 * (`agent_sessions_reference_basis_ck`) enforces that biconditional in both
 * directions — so this is the read-side statement of the same fact rather than a
 * second opinion about it. Expressed as an allow-set for the reason
 * `isDisplayableCost` is: a basis added later is refused by default instead of
 * inheriting permission to be rendered as money.
 */
const DISPLAYABLE: ReadonlySet<ReferenceBasis> = new Set<ReferenceBasis>([ReferenceBasis.Listed]);

/** Does this basis carry a renderable reference figure? */
export function isDisplayableReference(basis: ReferenceBasis): boolean {
  return DISPLAYABLE.has(basis);
}
